<script setup>
import { ref, onMounted, computed } from "vue"
import iot from "@/api/iot"
import IoTDeviceForm from "./IoTDeviceForm.vue"
import { useDialogStore } from "@/stores/dialog"
import { exportToCsv } from "@/utils/exportUtils"
import { useRouter } from "vue-router"

// state
const devices = ref([])
const loading = ref(false)
const error = ref("")
const search = ref("")
const selectedDeviceType = ref(null)
const exportingDeviceId = ref(null)
const showForm = ref(false)
const editItem = ref(null)
const dialog = useDialogStore()
const router = useRouter()

// Data viewer dialog state — surfaces the raw Cosmos readings for a single device
const showData = ref(false)
const dataItem = ref(null)
const dataPoints = ref([])
const dataLoading = ref(false)
const dataError = ref("")
// The data dialog uses its own date range, defaulted from the page-level range when opened.
// This lets users tweak the range without affecting the main grid's filters.
const dataFrom = ref(null)
const dataTo = ref(null)

const dataHeaders = [
  { title: "Date / Time", key: "dateTime", sortable: true },
  { title: "Value", key: "value", sortable: true, align: "end" },
  { title: "Voltage", key: "voltage", sortable: true, align: "end" },
]

const dataSummary = computed(() => {
  const pts = dataPoints.value
  if (!pts.length) return { count: 0, total: 0, byDay: 0 }
  const total = pts.reduce((s, p) => s + (Number(p.value) || 0), 0)
  // Days actually seen in the response (not range width — useful for "missing days" diagnostics)
  const uniqueDays = new Set(
    pts.map((p) => new Date(p.ts).toISOString().slice(0, 10))
  )
  return { count: pts.length, total: Math.round(total), byDay: uniqueDays.size }
})

// shared date range (defaults: last 7 days)
const to = ref(new Date())
const from = ref(new Date(Date.now() - 7 * 24 * 3600 * 1000))

function fmtInput(dt) {
  const pad = (n) => String(n).padStart(2, "0")
  return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`
}
function parseInput(v) { return new Date(v) }

// crude mapper from DB type string → API type
function toDeviceType(device) {
  const containerId = device.cosmosContainerId || "";
  if (/pip|peoplesense/i.test(containerId)) return "PIP";
  if (/ding/i.test(containerId)) return "DING";
  // Eco-Counter docs share the per-bucket shape with PIP, so route through PIP server-side.
  if (/ecocounter/i.test(containerId)) return "PIP";
  return "UNKNOWN"; // Better fallback
}

async function load() {
  loading.value = true; error.value = ""
  try {
    // Devices + freshness fired in parallel — freshness is best-effort, never blocks the list.
    const [devResp, freshness] = await Promise.all([
      iot.getDevices(),
      iot.getDevicesFreshness().catch((err) => {
        console.warn("Freshness fetch failed — Last Reading column will be empty", err)
        return {}
      }),
    ])

    const rawDevices = devResp.data || devResp  // Handle both response types
    // Normalize property names to camelCase (handle both PascalCase and camelCase from API)
    devices.value = rawDevices.map(d => {
      const deviceId = d.deviceId || d.DeviceId || ''
      return {
        deviceId,
        deviceName: d.deviceName || d.DeviceName || '',
        deviceType: d.deviceType || d.DeviceType || '',
        cosmosContainerId: d.cosmosContainerId || d.CosmosContainerId || '',
        deviceRowId: d.deviceRowId || d.DeviceRowId || 0,
        installationDateUtc: d.installationDateUtc || d.InstallationDateUtc || null,
        latitude: d.latitude ?? d.Latitude ?? null,
        longitude: d.longitude ?? d.Longitude ?? null,
        lastReadingUtc: (freshness && freshness[deviceId]) || null,
      }
    })
  } catch (err) {
    error.value = err.message || String(err)
  } finally {
    loading.value = false
  }
}

// Relative-time formatter for the Last Reading column. Returns "—" when never seen,
// "1h ago" / "3d ago" otherwise. Pure function, no Vue reactivity needed.
function relativeAge(iso) {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t)) return null
  const diffMs = Date.now() - t
  if (diffMs < 0) return "just now"
  const mins = Math.floor(diffMs / 60000)
  if (mins < 1) return "just now"
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.floor(months / 12)}y ago`
}

// Traffic-light colour for the Last Reading chip.
//   green:  ≤ 25h   (one day + grace)
//   amber:  ≤ 7d
//   red:    older
//   grey:   never
function freshnessColor(iso) {
  if (!iso) return "grey"
  const ageMs = Date.now() - new Date(iso).getTime()
  if (!Number.isFinite(ageMs)) return "grey"
  const hrs = ageMs / 3600000
  if (hrs <= 25) return "success"
  if (hrs <= 7 * 24) return "warning"
  return "error"
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}

async function exportPdf(dev) {
  try {
    exportingDeviceId.value = dev.deviceId
  const payload = {
    deviceId: dev.deviceId,
    deviceType: toDeviceType(dev),
    fromUtcMs: from.value.getTime(),
    toUtcMs: to.value.getTime(),
  }
  const res = await iot.exportPdf(payload)
  // axios with responseType: "blob" returns { data: Blob, headers, ... }
  const blob = res?.data instanceof Blob ? res.data : (res instanceof Blob ? res : null)
  if (!blob) throw new Error("Invalid export response")
  const contentDisp = res?.headers && (res.headers["content-disposition"] ?? res.headers["Content-Disposition"])
  const filename = contentDisp?.match(/filename="?([^";\n]+)"?/)?.[1]
    ?? `${dev.deviceId}_${payload.deviceType}_${new Date().toISOString().slice(0,16).replace(/[:]/g,"")}.pdf`
  downloadBlob(blob, filename)
  } catch (err) {
    let msg = err.message || "Failed to export PDF"
    if ((err.response?.status === 400 || err.response?.status === 500) && err.response?.data != null) {
      try {
        const data = err.response.data
        const text = typeof data === "string" ? data : (data instanceof Blob ? await data.text() : JSON.stringify(data))
        const parsed = typeof text === "string" && text.startsWith("{") ? JSON.parse(text) : null
        if (parsed?.detail) msg = parsed.detail
        else if (parsed?.title) msg = parsed.title
        else if (parsed?.error) msg = parsed.error + (parsed.detail ? ": " + parsed.detail : "")
        else if (typeof text === "string" && text.length < 300) msg = text
      } catch (_) { /* keep default msg */ }
    }
    error.value = msg
  } finally {
    exportingDeviceId.value = null
  }
}

const deviceTypeOptions = computed(() =>
  [...new Set(devices.value.map(d => d.deviceType).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b))
)

const filteredDevices = computed(() => {
  const query = search.value?.trim().toLowerCase() || ""
  return devices.value.filter(d => {
    const matchesType = !selectedDeviceType.value || d.deviceType === selectedDeviceType.value
    const matchesSearch = !query ||
      (d.deviceName || "").toLowerCase().includes(query) ||
      (d.deviceId || "").toLowerCase().includes(query) ||
      (d.deviceType || "").toLowerCase().includes(query)
    return matchesType && matchesSearch
  })
})

const headers = [
  { title: "Name", key: "deviceName", sortable: true },
  { title: "Device ID", key: "deviceId", sortable: true },
  { title: "Type", key: "deviceType", sortable: true },
  { title: "Last Reading", key: "lastReadingUtc", sortable: true },
  { title: "Latitude", key: "latitude", sortable: true },
  { title: "Longitude", key: "longitude", sortable: true },
  { title: "Actions", key: "actions", sortable: false, align: "end", width: "200" },
]

function openForm(item = null) {
  editItem.value = item
  showForm.value = true
}

function onRowClick(event, payload) {
  const item = payload?.item ?? payload
  if (item) openForm(item)
}

function openWaterLogger(device) {
  router.push({ name: "IoTDashboard", query: { deviceId: device.deviceId } })
}

async function handleDelete(device) {
  const result = await dialog.confirm({
    title: "Delete Device",
    message: `Are you sure you want to delete "${device.deviceName || device.deviceId}"?`,
    variant: "warn",
    buttons: "ok-cancel"
  })
  
  if (result !== "ok") return

  try {
    loading.value = true
    await iot.deleteDevice(device.deviceRowId)
    await load()
  } catch (err) {
    error.value = err.response?.data?.message || err.message || "Failed to delete device"
  } finally {
    loading.value = false
  }
}

function handleSaved() {
  load()
}

async function openData(device) {
  dataItem.value = device
  dataPoints.value = []
  dataError.value = ""
  // Seed the dialog's date range from the page-level filter so behaviour is consistent.
  dataFrom.value = new Date(from.value)
  dataTo.value = new Date(to.value)
  showData.value = true
  await fetchData()
}

async function fetchData() {
  if (!dataItem.value) return
  dataLoading.value = true
  dataError.value = ""
  try {
    const payload = {
      deviceId: dataItem.value.deviceId,
      deviceType: toDeviceType(dataItem.value),
      fromUtcMs: dataFrom.value.getTime(),
      toUtcMs: dataTo.value.getTime(),
      customerId: "",
    }
    const res = await iot.getSeries(payload)
    const series = res.data || res || []
    dataPoints.value = series
      .map((p) => ({
        ts: Number(p.ts),
        dateTime: new Date(Number(p.ts)).toLocaleString("en-GB"),
        value: p.value,
        voltage: p.voltage,
      }))
      .sort((a, b) => b.ts - a.ts)
  } catch (err) {
    dataError.value = err?.response?.data?.detail || err?.message || "Failed to load data"
  } finally {
    dataLoading.value = false
  }
}

function exportDataCsv() {
  if (!dataPoints.value.length || !dataItem.value) return
  const fileName = `Data_${dataItem.value.deviceId}_${new Date().toISOString().slice(0, 10)}`
  exportToCsv(
    fileName,
    [
      { key: "dateTime", label: "Date / Time" },
      { key: "value", label: "Value" },
      { key: "voltage", label: "Voltage" },
    ],
    dataPoints.value
  )
}

function exportListCsv() {
  exportToCsv("IoTDevices", [
    { key: "deviceName", label: "Name" },
    { key: "deviceId",   label: "Device ID" },
    { key: "deviceType", label: "Type" },
    { key: "latitude",   label: "Latitude" },
    { key: "longitude",  label: "Longitude" },
    { key: "installationDateUtc", label: "Installed" },
  ], filteredDevices.value.map((d) => ({
    ...d,
    installationDateUtc: d.installationDateUtc
      ? new Date(d.installationDateUtc).toLocaleDateString("en-GB")
      : "",
  })));
}

onMounted(load)
</script>

<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card-title class="ww-page-header">
          <div class="ww-page-heading">
            <div class="ww-page-title">IoT Devices</div>
            <div class="ww-page-subtitle">View and manage your IoT devices.</div>
          </div>
          <div class="d-flex ga-2">
            <v-btn color="success" variant="outlined" @click="exportListCsv">
              <v-icon start icon="mdi-file-excel" /> Export to Excel
            </v-btn>
            <v-btn color="primary" @click="openForm()">
              <v-icon start icon="mdi-plus" /> Create New Device
            </v-btn>
          </div>
        </v-card-title>
      </v-col>
    </v-row>

    <v-row style="margin-top: 0px">
      <v-col cols="12">
        <v-card elevation="3">
          <!-- Card header with filters -->
          <v-toolbar flat color="white" dense class="px-4 align-center">
            <v-toolbar-title class="pa-0 ma-0 d-flex align-center">
              <v-text-field
                v-model="search"
                placeholder="Search devices"
                clearable
                prepend-inner-icon="mdi-magnify"
                hide-details
                dense
                density="compact"
                solo
                class="mx-2 pa-0 search-field"
                style="max-width: 300px"
              />
            </v-toolbar-title>
            <v-spacer />
            <div class="d-flex align-center" style="gap: 12px">
              <v-select
                v-model="selectedDeviceType"
                :items="deviceTypeOptions"
                label="IoT type"
                placeholder="All IoT types"
                clearable
                density="compact"
                hide-details
                variant="outlined"
                style="min-width: 190px; max-width: 240px"
              />
              <v-text-field
                :model-value="fmtInput(from)"
                @update:model-value="from = parseInput($event)"
                type="datetime-local"
                label="From"
                density="compact"
                hide-details
                variant="outlined"
                style="max-width: 220px"
              />
              <v-text-field
                :model-value="fmtInput(to)"
                @update:model-value="to = parseInput($event)"
                type="datetime-local"
                label="To"
                density="compact"
                hide-details
                variant="outlined"
                style="max-width: 220px"
              />
              <v-btn
                color="primary"
                @click="load"
                :loading="loading"
                :disabled="loading"
                prepend-icon="mdi-refresh"
              >
                Refresh
              </v-btn>
            </div>
          </v-toolbar>

          <v-alert
            v-if="error"
            type="error"
            variant="tonal"
            dismissible
            class="ma-4"
            @update:model-value="error = ''"
          >
            {{ error }}
          </v-alert>

          <!-- Data table -->
          <v-data-table
            :headers="headers"
            :items="filteredDevices"
            :loading="loading"
            class="elevation-1 iot-devices-table"
            item-key="deviceId"
            density="comfortable"
            loading-text="Loading devices…"
            no-data-text="No devices found."
            hide-default-footer
            striped
            :items-per-page="-1"
            @click:row="onRowClick"
          >
            <template #item.deviceName="{ item }">
              <div class="font-weight-medium">
                {{ item.deviceName || item.deviceId }}
      </div>
            </template>
            <template #item.deviceId="{ item }">
      <div>
                {{ item.deviceId || 'N/A' }}
      </div>
            </template>
            <template #item.latitude="{ item }">
              <span>{{ item.latitude != null ? Number(item.latitude).toFixed(6) : '—' }}</span>
            </template>
            <template #item.longitude="{ item }">
              <span>{{ item.longitude != null ? Number(item.longitude).toFixed(6) : '—' }}</span>
            </template>
            <template #item.deviceType="{ item }">
              <v-chip
                :color="item.deviceType === 'PeopleSense' ? 'success' : 'info'"
                variant="elevated"
                size="small"
                label
              >
                {{ item.deviceType }}
              </v-chip>
            </template>
            <template #item.lastReadingUtc="{ item }">
              <v-tooltip
                v-if="item.lastReadingUtc"
                :text="new Date(item.lastReadingUtc).toLocaleString('en-GB')"
                location="top"
              >
                <template #activator="{ props }">
                  <v-chip
                    v-bind="props"
                    :color="freshnessColor(item.lastReadingUtc)"
                    variant="flat"
                    size="small"
                    label
                  >
                    {{ relativeAge(item.lastReadingUtc) }}
                  </v-chip>
                </template>
              </v-tooltip>
              <v-chip v-else color="grey" variant="flat" size="small" label>
                never
              </v-chip>
            </template>
            <template #item.actions="{ item }">
              <div class="d-flex align-center" style="gap: 8px">
                <v-btn
                  v-if="item.deviceType === 'Water Logger'"
                  color="primary"
                  variant="text"
                  size="small"
                  prepend-icon="mdi-water-thermometer"
                  @click.stop="openWaterLogger(item)"
                >
                  Dashboard
                </v-btn>
                <v-btn
                  icon="mdi-pencil"
                  variant="text"
                  size="small"
                  color="primary"
                  @click.stop="openForm(item)"
                  title="Edit"
                />
                <v-btn
                  v-if="item.deviceType !== 'Water Logger'"
                  icon="mdi-table-eye"
                  variant="text"
                  size="small"
                  color="info"
                  @click.stop="openData(item)"
                  title="View readings (data)"
                />
                <v-btn
                  icon="mdi-delete"
                  variant="text"
                  size="small"
                  color="error"
                  @click.stop="handleDelete(item)"
                  title="Delete"
                />
                <v-btn
                  v-if="item.deviceType !== 'Water Logger'"
                  color="primary"
                  variant="text"
                  size="small"
                  prepend-icon="mdi-file-pdf-box"
                  @click.stop="exportPdf(item)"
                  :loading="exportingDeviceId === item.deviceId"
                  :disabled="!!exportingDeviceId && exportingDeviceId !== item.deviceId"
                >
                  Export PDF
                </v-btn>
    </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Device Form Dialog -->
    <IoTDeviceForm
      v-model="showForm"
      :edit-item="editItem"
      @saved="handleSaved"
    />

    <!-- Device Data Viewer Dialog -->
    <v-dialog v-model="showData" max-width="960" scrollable>
      <v-card v-if="dataItem">
        <v-card-title class="d-flex align-center">
          <div>
            <div>Readings — {{ dataItem.deviceName || dataItem.deviceId }}</div>
            <div class="text-caption text-medium-emphasis">{{ dataItem.deviceId }}</div>
          </div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" @click="showData = false" />
        </v-card-title>

        <v-divider />

        <v-card-text style="max-height: 70vh">
          <v-row dense align="center" class="mb-2">
            <v-col cols="12" sm="4">
              <v-text-field
                :model-value="dataFrom ? fmtInput(dataFrom) : ''"
                @update:model-value="dataFrom = parseInput($event)"
                type="datetime-local"
                label="From"
                density="compact"
                hide-details
                variant="outlined"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                :model-value="dataTo ? fmtInput(dataTo) : ''"
                @update:model-value="dataTo = parseInput($event)"
                type="datetime-local"
                label="To"
                density="compact"
                hide-details
                variant="outlined"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-btn
                color="primary"
                block
                @click="fetchData"
                :loading="dataLoading"
                prepend-icon="mdi-refresh"
              >
                Refresh
              </v-btn>
            </v-col>
          </v-row>

          <v-alert
            v-if="dataError"
            type="error"
            variant="tonal"
            dismissible
            class="my-2"
            @update:model-value="dataError = ''"
          >
            {{ dataError }}
          </v-alert>

          <v-row dense class="mb-2" v-if="!dataLoading && dataPoints.length">
            <v-col cols="4">
              <v-card variant="tonal" color="info" density="compact">
                <v-card-text class="text-center pa-2">
                  <div class="text-h6">{{ dataSummary.count }}</div>
                  <div class="text-caption">Readings</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="4">
              <v-card variant="tonal" color="success" density="compact">
                <v-card-text class="text-center pa-2">
                  <div class="text-h6">{{ dataSummary.total.toLocaleString() }}</div>
                  <div class="text-caption">Total Count</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="4">
              <v-card variant="tonal" color="warning" density="compact">
                <v-card-text class="text-center pa-2">
                  <div class="text-h6">{{ dataSummary.byDay }}</div>
                  <div class="text-caption">Distinct Days</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-data-table
            v-if="!dataLoading"
            :headers="dataHeaders"
            :items="dataPoints"
            density="compact"
            :items-per-page="50"
            :items-per-page-options="[25, 50, 100, 500, -1]"
            no-data-text="No readings in this range."
            class="elevation-1"
          >
            <template #item.value="{ item }">
              <span class="font-weight-medium">{{ item.value != null ? Number(item.value).toLocaleString() : '—' }}</span>
            </template>
            <template #item.voltage="{ item }">
              <span>{{ item.voltage != null ? Number(item.voltage).toFixed(2) : '—' }}</span>
            </template>
          </v-data-table>

          <div v-else class="text-center pa-6">
            <v-progress-circular indeterminate color="primary" />
            <div class="mt-2 text-medium-emphasis">Loading readings…</div>
          </div>
        </v-card-text>

        <v-divider />
        <v-card-actions>
          <v-btn
            variant="outlined"
            color="success"
            prepend-icon="mdi-file-excel"
            :disabled="!dataPoints.length"
            @click="exportDataCsv"
          >
            Export to CSV
          </v-btn>
          <v-spacer />
          <v-btn variant="text" @click="showData = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style scoped>
.iot-devices-table :deep(.v-data-table__tr) {
  cursor: pointer;
}
.iot-devices-table :deep(.v-data-table__tr:hover) {
  background-color: rgba(0, 0, 0, 0.04) !important;
}
</style>
